/**
 * Whether this desk can actually speak, decided before a customer is on the line.
 *
 * A browser will not hand a page a microphone until somebody says so, and the moment it
 * asks is the worst possible moment: a call is ringing, a customer is waiting, and the
 * dispatcher is looking at a permission dialog instead of the order form. Worse, the
 * failure does not announce itself as a permission problem — JsSIP reports every
 * `getUserMedia` rejection under one cause, so a refused prompt and a machine with no
 * microphone at all produce the same message, and an unanswered prompt produces no
 * message at all until the far end gives up and the log says the call was cancelled.
 *
 * So the question is asked at the start of the shift, when the answer is cheap.
 *
 * The classification lives here, apart from the hook that gathers the facts, because it is
 * the part with the wrong answers in it and `tests/js` compiles without DOM types.
 */

/** What the desk's microphone situation is, in the terms the dispatcher needs. */
export type MicrophoneReadiness =
  /** Granted, with a device behind it. Nothing to say. */
  | 'ready'
  /** Never asked. One click fixes it, and the click has to be a person's. */
  | 'askable'
  /** Refused. A page cannot re-ask; only the browser's own settings can undo this. */
  | 'refused'
  /** No capture device at all. Permission is beside the point. */
  | 'missing'
  /** The browser will not say. Asking is the only way to find out. */
  | 'unknown';

/** The three answers the Permissions API gives, plus null for "it would not say". */
export type PermissionAnswer = 'granted' | 'prompt' | 'denied' | null;

/**
 * Turns what the browser reported into what the desk needs to know.
 *
 * Device count is checked first and beats every permission state, because a grant on a
 * machine with no microphone is a grant that changes nothing — and that is a real case
 * rather than a hypothetical one: a desktop whose analog jack is empty enumerates no audio
 * input at all, and the resulting failure has been mistaken for a refused prompt before.
 */
export function classify(
  permission: PermissionAnswer,
  audioInputs: number,
): MicrophoneReadiness {
  if (audioInputs <= 0) return 'missing';

  switch (permission) {
    case 'granted':
      return 'ready';
    case 'denied':
      return 'refused';
    case 'prompt':
      return 'askable';
    default:
      return 'unknown';
  }
}

/**
 * Turns a failed request for the microphone into the same vocabulary.
 *
 * The names come from the specification rather than from any one browser, and the two that
 * matter are worth telling apart in the interface: `NotAllowedError` is a person saying no
 * and can only be undone in the browser's settings, while `NotFoundError` is hardware that
 * is not there and cannot be undone in the browser at all. Offering "try again" for the
 * second would be offering a button that can never work.
 */
export function fromError(error: unknown): MicrophoneReadiness {
  const name = typeof error === 'object' && error !== null && 'name' in error
    ? String((error as { name?: unknown }).name)
    : '';

  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'refused';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'missing';
    default:
      return 'unknown';
  }
}

/** Whether the dispatcher should be told something, rather than left alone. */
export function needsAttention(readiness: MicrophoneReadiness): boolean {
  return readiness !== 'ready';
}

/**
 * Whether offering a button that asks for the microphone would do any good.
 *
 * Not for `refused`, where the browser has already decided and will not re-ask — a button
 * there would do nothing at all, which is worse than no button. Not for `missing` either.
 * `unknown` gets one, because asking is the only way left to find out.
 */
export function canAsk(readiness: MicrophoneReadiness): boolean {
  return readiness === 'askable' || readiness === 'unknown';
}

/** The message to show, as a translation key. One per state, and none when ready. */
export function messageKey(readiness: MicrophoneReadiness): string | null {
  return readiness === 'ready' ? null : `softphone.microphoneAccess.${readiness}`;
}
